/**
 * Лимиты медиа и локальная проверка файла.
 *
 * CC §8.2 п. 1: единственный источник лимитов — `GET /config → limits`
 * (решение D-39). Дублировать значения в коде клиента ЗАПРЕЩЕНО, поэтому
 * здесь нет ни одной числовой константы лимита — только логика проверки.
 *
 * CC §8.2 п. 4: клиент ОБЯЗАН проверить тип, размер, длительность и размеры
 * изображения ДО `POST /media/uploads` — чтобы не расходовать серверный лимит
 * класса `media` на заведомо негодный файл.
 */
import type { Config, MediaLimit } from '@shared/api/types'

export type MediaPurpose = 'profile_photo' | 'chat_image' | 'chat_video' | 'chat_voice' | 'channel_photo'

/** §10.2 п. 2: верхняя граница стороны после уменьшения. */
export const MAX_DIMENSION_PX = 2048

/** §10.2 п. 3: качество JPEG при перекодировании. */
export const JPEG_QUALITY = 0.85

/**
 * §10.2 п. 1a: жёсткий потолок до попытки декодирования. Мобильные браузеры
 * ограничивают площадь canvas, и обработка такого файла даёт пустой холст.
 */
export const HARD_MEGAPIXEL_CAP = 50

export type RejectReason =
  | 'unsupported_type'
  | 'too_large'
  | 'too_long'
  | 'too_small'
  | 'too_many_pixels'
  | 'decode_failed'

export interface LocalRejection {
  reason: RejectReason
  /** Понятный текст для U-14 / C-20 — экран показывает его как есть. */
  message: string
}

/**
 * CC §8.2 п. 2: ключи лимитов совпадают со значениями `UploadCreate.purpose`.
 * `limits.photo` — устаревший алиас `profile_photo`, читать СЛЕДУЕТ `profile_photo`.
 */
export function limitFor(config: Config | null, purpose: MediaPurpose): MediaLimit | null {
  if (!config) return null

  switch (purpose) {
    case 'profile_photo':
      return config.limits.profile_photo ?? config.limits.photo ?? null
    // Аватар канала (изменение v1.7) — свой ключ лимитов с теми же значениями, что у профиля.
    case 'channel_photo':
      return config.limits.channel_photo ?? config.limits.profile_photo ?? null
    case 'chat_image':
      return config.limits.chat_image
    case 'chat_video':
      return config.limits.chat_video
    case 'chat_voice':
      return config.limits.chat_voice
    default:
      return null
  }
}

/** CC §8.1: `content_type` передаётся базовым MIME без параметров. */
export function baseMime(contentType: string): string {
  return contentType.split(';')[0]?.trim().toLowerCase() ?? contentType
}

export function isTypeAllowed(limit: MediaLimit, contentType: string): boolean {
  const base = baseMime(contentType)
  return limit.content_types.some((allowed) => baseMime(allowed) === base)
}

/** Список для атрибута `accept` (§10.1): строится из лимита, а не из кода. */
export function acceptAttribute(limit: MediaLimit | null): string {
  if (!limit) return ''
  return limit.content_types.map(baseMime).join(',')
}

/* ===================================================================== */
/* Проверки                                                               */
/* ===================================================================== */

export function checkType(limit: MediaLimit, file: File): LocalRejection | null {
  if (isTypeAllowed(limit, file.type)) return null

  return {
    reason: 'unsupported_type',
    message: `Такой формат не поддерживается. Подойдут: ${describeTypes(limit)}.`,
  }
}

export function checkSize(limit: MediaLimit, sizeBytes: number): LocalRejection | null {
  if (sizeBytes <= limit.max_bytes) return null

  return {
    reason: 'too_large',
    message: `Файл больше ${formatMegabytes(limit.max_bytes)}. Выберите файл поменьше.`,
  }
}

export function checkDuration(limit: MediaLimit, durationMs: number | null): LocalRejection | null {
  if (!limit.max_duration_sec || durationMs === null) return null
  if (durationMs <= limit.max_duration_sec * 1000) return null

  return {
    reason: 'too_long',
    message: `Длительность больше ${limit.max_duration_sec} с.`,
  }
}

/**
 * §10.2 п. 4: для `profile_photo` меньшая сторона результата ОБЯЗАНА остаться
 * не ниже `min_side_px`. Отсутствующее или нулевое значение — «не проверяется»
 * (CC §8.2 п. 3).
 */
export function checkMinSide(limit: MediaLimit, width: number, height: number): LocalRejection | null {
  const minSide = limit.min_side_px ?? 0
  if (!minSide) return null
  if (Math.min(width, height) >= minSide) return null

  return {
    reason: 'too_small',
    message: `Фотография слишком маленькая: нужна сторона не меньше ${minSide} px. Выберите другое фото.`,
  }
}

/** §10.2 п. 1a: отказ ДО декодирования, если мегапикселей заведомо слишком много. */
export function checkMegapixelsBeforeDecode(
  limit: MediaLimit,
  width: number,
  height: number,
): LocalRejection | null {
  const megapixels = (width * height) / 1_000_000
  const cap = Math.min(limit.max_megapixels ?? HARD_MEGAPIXEL_CAP, HARD_MEGAPIXEL_CAP)

  if (megapixels <= cap) return null

  return {
    reason: 'too_many_pixels',
    message: `Изображение слишком большое (${megapixels.toFixed(0)} Мп). Максимум — ${cap} Мп.`,
  }
}

/* ===================================================================== */
/* Тексты                                                                 */
/* ===================================================================== */

const TYPE_LABELS: Record<string, string> = {
  'image/jpeg': 'JPEG',
  'image/png': 'PNG',
  'image/webp': 'WebP',
  'image/heic': 'HEIC',
  'video/mp4': 'MP4',
  'video/quicktime': 'MOV',
  'audio/webm': 'WebM',
  'audio/ogg': 'OGG',
  'audio/mp4': 'M4A',
}

function describeTypes(limit: MediaLimit): string {
  const labels = limit.content_types.map((type) => TYPE_LABELS[baseMime(type)] ?? baseMime(type))
  return [...new Set(labels)].join(', ')
}

function formatMegabytes(bytes: number): string {
  const mb = bytes / (1024 * 1024)
  return mb >= 10 ? `${Math.round(mb)} МБ` : `${mb.toFixed(1)} МБ`
}

/** Понятный текст причины отклонения модерацией (CC §8.6 п. 2). */
export const MODERATION_REASON_TEXT: Record<string, string> = {
  nsfw: 'Содержимое не подходит для сервиса',
  no_face: 'На фотографии не видно лица',
  not_person: 'На фотографии нет человека',
  low_quality: 'Слишком низкое качество',
  prohibited_content: 'Запрещённое содержимое',
  age_safety: 'Не прошло проверку по возрасту и безопасности',
  contact_info: 'На изображении есть контактные данные',
  other: 'Не прошло проверку',
}
